import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useApiClient from '../../../services';
import { RootState } from '../../../state/features/reducers';
/**
 * Embeds a Grafana dashboard into a page.
 * See `defaultProps` for other parameters. */

type GrafanaDashboardProps = {
    src: string;
    title?: string;
    kioskMode?: string;
    height?: string;
    width?: string;
    style?: React.CSSProperties;
    className?: string;
    refreshKey?: any;
    isDarkMode?: boolean;
};

export const GrafanaDashboard = ({
    src,
    title,
    kioskMode = '1',
    height = '100%',
    width = '100%',
    style = {},
    className,
    refreshKey,
    isDarkMode = true,
}: GrafanaDashboardProps) => {
    const token = useSelector(
        (x: RootState) => x?.auth?.user?.accessToken,
    );
    // Set the theme based on the dark mode setting-
    const theme = isDarkMode ? 'dark' : 'light';

    const api = useApiClient();
    const [url, setURL] = useState('');
    useEffect(() => {
        // This is a bit of hack to prevent the user from using grafana keyboard shortcuts in the embedded view
        // it would be preferable to attach intercepting keypress event listeners but that required grafana on the same domain
        // solution from: https://github.com/grafana/grafana/issues/13706#issuecomment-1263109009
        const listener = window.addEventListener('blur', (e) => {
            setTimeout(function () {
                window.focus();
            }, 0);
        });
        const refreshInterval = 45 * 60 * 1000; // our auth tokens expire after 1 hour, so refresh every 45 minutes
        const refreshIntervalFunc = setInterval(async () => {
            if (token) {
                const isExpired = api?.checkSessionExpiry(token);
                if (isExpired) {
                    await api?.refreshToken();
                }
            }
        }, refreshInterval);
        return () => {
            if (listener != null) {
                window.removeEventListener('blur', listener);
                clearInterval(refreshIntervalFunc);
            }
        };
    }, []);

    useEffect(() => {
        const origin = api?.getOrigin();
        if (!origin || !src) return;
        let tmpUrl = src;

        // Relative paths (from the edge machine's rules controller) need the origin prepended
        // before the absolute-URL checks below can work correctly.
        if (tmpUrl.startsWith('/')) {
            tmpUrl = origin + tmpUrl;
        }

        // All iframe URLs must match Grafana's React Router basename, which is derived
        // from GF_SERVER_ROOT_URL: .../customer-portal/api/v1/grafana-proxy/grafana/
        // Target format: https://<host>/customer-portal/api/v1/grafana-proxy/grafana/<path>
        //
        // Normalizes any of these database URL formats to the target:
        //   Format A (old proxy): https://<host>/grafana/customer-portal/api/v1-proxy/<path>
        //   Format B (direct):    https://<host>/grafana/<path>
        //   Format C (new, no /grafana/): https://<host>/customer-portal/api/v1/grafana-proxy/<path>
        //   Format D (correct):   https://<host>/customer-portal/api/v1/grafana-proxy/grafana/<path>
        const PROXY_WITH_GRAFANA = origin + '/customer-portal/api/v1/grafana-proxy/grafana';

        if (!tmpUrl.startsWith(PROXY_WITH_GRAFANA)) {
            // Reduce to a clean relative path, then rebuild with the correct prefix.
            if (tmpUrl.startsWith(origin)) {
                tmpUrl = tmpUrl.slice(origin.length);
            }
            if (tmpUrl.startsWith('/grafana')) {
                tmpUrl = tmpUrl.slice('/grafana'.length);
            }
            const OLD_PROXY_BASE = '/customer-portal/api/v1-proxy';
            if (tmpUrl.startsWith(OLD_PROXY_BASE)) {
                tmpUrl = tmpUrl.slice(OLD_PROXY_BASE.length);
            }
            // Strip new proxy base without /grafana/ (Format C already in proxy but missing it).
            const NEW_PROXY_BASE = '/customer-portal/api/v1/grafana-proxy';
            if (tmpUrl.startsWith(NEW_PROXY_BASE)) {
                tmpUrl = tmpUrl.slice(NEW_PROXY_BASE.length);
            }
            tmpUrl = PROXY_WITH_GRAFANA + tmpUrl;
        }

        const separator = tmpUrl.includes('?') ? '&' : '?';
        tmpUrl += `${separator}theme=${theme}&kiosk=${kioskMode}`;
        setURL(tmpUrl);
    }, [src, isDarkMode]);

    return (
        <iframe
            key={refreshKey}
            title={title}
            id={title}
            src={url}
            width={width}
            height={height}
            style={{ border: 'none', ...style }}
            className={className}
            sandbox="allow-scripts allow-same-origin"
        />
    );
};
